'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useMemo, useState } from 'react';
import { History, Sparkles } from 'lucide-react';
import { useQueryState } from 'nuqs';
import z from 'zod';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { useAppForm } from '~/features/form/hooks';
import { SubmitButton } from '~/features/form/subscribers';
import { useActivityAiActiveChat } from './useActivityAiActiveChat';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from './ai-search-analytics';
import { useActivityAiChatStore } from './activity-ai-chat-store';
import { RecentChatsList } from './RecentChatsList';
import { SUGGESTION_DESCRIPTORS } from './ai-search-suggestions';
import { ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS, ACTIVITY_SEARCH_MODE_QUERY_PARAMS } from './shared';
import { useActivitySearchMode } from './useActivitySearchMode';
import { AiSearchBar } from './AiSearchBar';
const AI_PROMPT_VALIDATOR = z.object({
  prompt: z.string().trim().min(1)
});

/** AI search entry UI (parallel to pill filter bar). Wires up later to retrieval / streaming. */
export const ActivityAiSearchFilterPanel: React.FC<{
  className?: string;
  /** Visual surface the panel sits on. 'dark' = hero (default), 'light' = white card (e.g. NotFound). */
  surface?: 'dark' | 'light';
}> = t0 => {
  const $ = _c(134);
  const {
    className,
    surface: t1
  } = t0;
  const surface = t1 === undefined ? "dark" : t1;
  const i18n = useI18n();
  const isLight = surface === "light";
  const {
    mode,
    setMode
  } = useActivitySearchMode();
  const aiChat = useActivityAiActiveChat();
  const analytics = useAiSearchAnalytics();
  const threads = useActivityAiChatStore(_temp);
  let t2;
  if ($[0] !== threads) {
    t2 = Object.values(threads).sort(_temp2);
    $[0] = threads;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sortedThreads = t2;
  const [historyOpen, setHistoryOpen] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ACTIVITY_SEARCH_MODE_QUERY_PARAMS.startPrompt.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [startPromptParam, setStartPromptParam] = useQueryState("startPrompt", t3);
  const [seededPrompt, setSeededPrompt] = useState("");
  let t4;
  if ($[3] !== aiChat || $[4] !== setMode) {
    t4 = prompt => {
      setMode("ai");
      if (aiChat.chatId.length > 0) {
        aiChat.startNewChat(prompt);
      } else {
        aiChat.sendMessage(prompt);
      }
    };
    $[3] = aiChat;
    $[4] = setMode;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handlePromptSubmit = t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      prompt: ""
    };
    t6 = {
      onChange: AI_PROMPT_VALIDATOR,
      onSubmit: AI_PROMPT_VALIDATOR
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  const form = useAppForm({
    defaultValues: t5,
    validators: t6,
    onSubmit(t7) {
      const {
        value
      } = t7;
      if (isLight) {
        analytics[AI_SEARCH_EVENTS.EMPTY_STATE_HANDOFF]({
          session_id: null
        });
      }
      setMode("ai");
      if (aiChat.chatId.length > 0) {
        aiChat.startNewChat(value.prompt);
      } else {
        aiChat.sendMessage(value.prompt);
      }
      form.setFieldValue("prompt", "");
    }
  });
  let t8;
  if ($[8] !== form || $[9] !== setMode || $[10] !== setStartPromptParam || $[11] !== startPromptParam) {
    t8 = () => {
      const seed = startPromptParam?.trim();
      if (!seed) {
        return;
      }
      setSeededPrompt(seed);
      form.setFieldValue("prompt", seed);
      setMode("ai");
      setStartPromptParam(null);
    };
    $[8] = form;
    $[9] = setMode;
    $[10] = setStartPromptParam;
    $[11] = startPromptParam;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== form || $[14] !== setMode || $[15] !== setStartPromptParam || $[16] !== startPromptParam) {
    t9 = [startPromptParam, setMode, setStartPromptParam, form];
    $[13] = form;
    $[14] = setMode;
    $[15] = setStartPromptParam;
    $[16] = startPromptParam;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  useLayoutEffect(t8, t9);
  if (!isLight) {
    let t10;
    if ($[18] !== className) {
      t10 = cn("flex w-full max-w-xl items-center rounded-full bg-white p-2 shadow-2xl shadow-black/5", className);
      $[18] = className;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== setMode) {
      t11 = next => {
        setMode(next);
        if (next !== "ai" && typeof window !== "undefined") {
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      };
      $[20] = setMode;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== analytics) {
      t12 = next_0 => {
        if (next_0) {
          analytics[AI_SEARCH_EVENTS.HISTORY_OPENED]({
            session_id: null,
            source: "hero"
          });
        }
        setHistoryOpen(next_0);
      };
      $[22] = analytics;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = cn("flex size-9 shrink-0 items-center justify-center rounded-full text-stone-500 transition-colors", "hover:bg-stone-100 hover:text-stone-800", "data-[state=open]:bg-stone-100 data-[state=open]:text-stone-800");
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== i18n) {
      t14 = i18n._("activity.search.ai.historyTrigger", undefined, {
        message: "Previous chats"
      });
      $[25] = i18n;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <History className="size-4" strokeWidth={2} aria-hidden={true} />;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== t14) {
      t16 = <PopoverTrigger asChild={true}><button type="button" className={t13} aria-label={t14}>{t15}</button></PopoverTrigger>;
      $[28] = t14;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== i18n) {
      t17 = i18n._("activity.search.ai.historyHeading", undefined, {
        message: "Recent chats"
      });
      $[30] = i18n;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    let t18;
    if ($[32] !== t17) {
      t18 = <div className="px-3 py-2"><p className="text-xs font-semibold text-foreground">{t17}</p></div>;
      $[32] = t17;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    let t19;
    if ($[34] !== aiChat || $[35] !== analytics || $[36] !== i18n || $[37] !== setMode || $[38] !== sortedThreads) {
      t19 = sortedThreads.length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">{i18n._("activity.search.ai.historyPreviewNotice", undefined, {
          message: "Preview only \u2014 history is not saved yet."
        })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={aiChat.chatId} showSearch={true} variant="popover" className="px-3 py-2" onSelect={id => {
        analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
          session_id: id,
          source: "hero_popover"
        });
        setHistoryOpen(false);
        setMode("ai");
        aiChat.openThread(id);
      }} />;
      $[34] = aiChat;
      $[35] = analytics;
      $[36] = i18n;
      $[37] = setMode;
      $[38] = sortedThreads;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    let t20;
    if ($[40] !== t18 || $[41] !== t19) {
      t20 = <PopoverContent align="end" sideOffset={12} className="w-[min(100vw-2rem,22rem)] p-0 shadow-lg">{t18}{t19}</PopoverContent>;
      $[40] = t18;
      $[41] = t19;
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    let t21;
    if ($[43] !== historyOpen || $[44] !== t12 || $[45] !== t16 || $[46] !== t20) {
      t21 = <Popover open={historyOpen} onOpenChange={t12}>{t16}{t20}</Popover>;
      $[43] = historyOpen;
      $[44] = t12;
      $[45] = t16;
      $[46] = t20;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    let t22;
    if ($[48] !== i18n) {
      t22 = i18n._("activity.search.ai.placeholderPrefix", undefined, {
        message: "Zoek naar"
      });
      $[48] = i18n;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== i18n) {
      t23 = SUGGESTION_DESCRIPTORS.map(s_0 => i18n._(s_0.id, undefined, {
        message: s_0.message
      }));
      $[50] = i18n;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== i18n) {
      t24 = i18n._("activity.search.ai.searchLabel", undefined, {
        message: "AI zoeken"
      });
      $[52] = i18n;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    let t25;
    if ($[54] !== i18n) {
      t25 = i18n._("activity.search.ai.mobileCtaStart", undefined, {
        message: "Zoeken met AI"
      });
      $[54] = i18n;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    let t26;
    if ($[56] !== handlePromptSubmit || $[57] !== mode || $[58] !== seededPrompt || $[59] !== t11 || $[60] !== t21 || $[61] !== t22 || $[62] !== t23 || $[63] !== t24 || $[64] !== t25) {
      t26 = <AiSearchBar mode={mode} onModeChange={t11} onSubmit={handlePromptSubmit} initialValue={seededPrompt} trailing={t21} placeholderPrefix={t22} suggestions={t23} aiSearchLabel={t24} ariaLabel={t25} />;
      $[56] = handlePromptSubmit;
      $[57] = mode;
      $[58] = seededPrompt;
      $[59] = t11;
      $[60] = t21;
      $[61] = t22;
      $[62] = t23;
      $[63] = t24;
      $[64] = t25;
      $[65] = t26;
    } else {
      t26 = $[65];
    }
    let t27;
    if ($[66] !== t10 || $[67] !== t26) {
      t27 = <div className={t10}>{t26}</div>;
      $[66] = t10;
      $[67] = t26;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    return t27;
  }
  let t10;
  if ($[69] !== className) {
    t10 = cn("flex w-full flex-col items-center gap-2 sm:gap-3", className);
    $[69] = className;
    $[70] = t10;
  } else {
    t10 = $[70];
  }
  let t11;
  if ($[71] !== form) {
    t11 = e => {
      e.preventDefault();
      return form.handleSubmit();
    };
    $[71] = form;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  const t12 = isLight && "border border-stone-200/90";
  let t13;
  if ($[73] !== t12) {
    t13 = cn("relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl shadow-black/5 sm:p-4", t12);
    $[73] = t12;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Sparkles className="size-3.5 shrink-0 text-secondary sm:size-4" strokeWidth={2} aria-hidden={true} />;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== i18n) {
    t15 = i18n._("activity.search.ai.newPrompt", undefined, {
      message: "New search"
    });
    $[76] = i18n;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== t15) {
    t16 = <p className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-stone-500 sm:text-sm">{t14}{t15}</p>;
    $[78] = t15;
    $[79] = t16;
  } else {
    t16 = $[79];
  }
  let t17;
  if ($[80] !== analytics) {
    t17 = next_1 => {
      if (next_1) {
        analytics[AI_SEARCH_EVENTS.HISTORY_OPENED]({
          session_id: null,
          source: "hero"
        });
      }
      setHistoryOpen(next_1);
    };
    $[80] = analytics;
    $[81] = t17;
  } else {
    t17 = $[81];
  }
  let t18;
  let t19;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn("inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-stone-600 transition-colors", "hover:bg-stone-200 hover:text-stone-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary/60", "data-[state=open]:bg-stone-200 data-[state=open]:text-stone-900");
    t19 = <History className="size-3.5 shrink-0 opacity-80" strokeWidth={2} aria-hidden={true} />;
    $[82] = t18;
    $[83] = t19;
  } else {
    t18 = $[82];
    t19 = $[83];
  }
  let t20;
  if ($[84] !== i18n) {
    t20 = i18n._("activity.search.ai.historyTrigger", undefined, {
      message: "Previous chats"
    });
    $[84] = i18n;
    $[85] = t20;
  } else {
    t20 = $[85];
  }
  let t21;
  if ($[86] !== t20) {
    t21 = <PopoverTrigger asChild={true}><button type="button" className={t18}>{t19}{t20}</button></PopoverTrigger>;
    $[86] = t20;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  let t22;
  if ($[88] !== i18n) {
    t22 = i18n._("activity.search.ai.historyHeading", undefined, {
      message: "Recent chats"
    });
    $[88] = i18n;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  let t23;
  if ($[90] !== t22) {
    t23 = <div className="px-3 py-2"><p className="text-xs font-semibold text-foreground">{t22}</p></div>;
    $[90] = t22;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== aiChat || $[93] !== analytics || $[94] !== i18n || $[95] !== setMode || $[96] !== sortedThreads) {
    t24 = sortedThreads.length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">{i18n._("activity.search.ai.historyPreviewNotice", undefined, {
        message: "Preview only \u2014 history is not saved yet."
      })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={aiChat.chatId} showSearch={true} variant="popover" className="px-3 py-2" onSelect={id_0 => {
      analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
        session_id: id_0,
        source: "hero_popover"
      });
      setHistoryOpen(false);
      setMode("ai");
      aiChat.openThread(id_0);
    }} />;
    $[92] = aiChat;
    $[93] = analytics;
    $[94] = i18n;
    $[95] = setMode;
    $[96] = sortedThreads;
    $[97] = t24;
  } else {
    t24 = $[97];
  }
  let t25;
  if ($[98] !== t23 || $[99] !== t24) {
    t25 = <PopoverContent align="end" sideOffset={8} className="w-[min(100vw-2rem,22rem)] p-0 shadow-lg">{t23}{t24}</PopoverContent>;
    $[98] = t23;
    $[99] = t24;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  let t26;
  if ($[101] !== historyOpen || $[102] !== t17 || $[103] !== t21 || $[104] !== t25) {
    t26 = <Popover open={historyOpen} onOpenChange={t17}>{t21}{t25}</Popover>;
    $[101] = historyOpen;
    $[102] = t17;
    $[103] = t21;
    $[104] = t25;
    $[105] = t26;
  } else {
    t26 = $[105];
  }
  let t27;
  if ($[106] !== t16 || $[107] !== t26) {
    t27 = <div className="mb-2 flex items-start justify-between gap-2 sm:items-center">{t16}{t26}</div>;
    $[106] = t16;
    $[107] = t26;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== i18n) {
    t28 = i18n._("activity.search.ai.promptLabel", undefined, {
      message: "Describe your team building"
    });
    $[109] = i18n;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== t28) {
    t29 = <label className="sr-only" htmlFor="activity-ai-search-prompt">{t28}</label>;
    $[111] = t28;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== form || $[114] !== i18n) {
    t30 = field => <textarea id="activity-ai-search-prompt" name={field.name} value={field.state.value} onChange={e_0 => field.handleChange(e_0.target.value)} onBlur={field.handleBlur} onKeyDown={e_1 => {
      if (e_1.key === "Enter" && !e_1.shiftKey) {
        e_1.preventDefault();
        form.handleSubmit();
      }
    }} rows={3} placeholder={i18n._({
      id: "activity.search.ai.placeholder",
      message: "Describe your team building\u2026"
    })} className="w-full resize-none border-0 bg-transparent px-0 py-0 text-sm leading-snug text-stone-800 outline-none ring-0 transition-colors placeholder:text-stone-400 focus:ring-0 sm:text-base" />;
    $[113] = form;
    $[114] = i18n;
    $[115] = t30;
  } else {
    t30 = $[115];
  }
  let t31;
  if ($[116] !== form.Field || $[117] !== t30) {
    t31 = <form.Field name="prompt">{t30}</form.Field>;
    $[116] = form.Field;
    $[117] = t30;
    $[118] = t31;
  } else {
    t31 = $[118];
  }
  let t32;
  if ($[119] !== i18n) {
    t32 = i18n._("activity.search.ai.submit", undefined, {
      message: "Search team building"
    });
    $[119] = i18n;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  let t33;
  if ($[121] !== form || $[122] !== t32) {
    t33 = <div className="absolute bottom-3 right-3"><SubmitButton form={form} variant="tertiary" size="sm" className="rounded-full px-4 py-2 text-xs font-semibold shadow-sm sm:gap-2 sm:px-5 sm:text-sm">{t32}</SubmitButton></div>;
    $[121] = form;
    $[122] = t32;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] !== t13 || $[125] !== t27 || $[126] !== t29 || $[127] !== t31 || $[128] !== t33) {
    t34 = <div className={t13}>{t27}{t29}{t31}{t33}</div>;
    $[124] = t13;
    $[125] = t27;
    $[126] = t29;
    $[127] = t31;
    $[128] = t33;
    $[129] = t34;
  } else {
    t34 = $[129];
  }
  let t35;
  if ($[130] !== t10 || $[131] !== t11 || $[132] !== t34) {
    t35 = <form className={t10} onSubmit={t11}>{t34}</form>;
    $[130] = t10;
    $[131] = t11;
    $[132] = t34;
    $[133] = t35;
  } else {
    t35 = $[133];
  }
  return t35;
};
function _temp(s) {
  return s.threads;
}
function _temp2(a, b) {
  return b.createdAt - a.createdAt;
}